<template>
  <div>
    <div class="price-chart-header">
      <div class="price-chart-header__price">
        {{ formattedPrice }}
      </div>
      <div class="price-chart-header__chg24h">
        <span v-if="priceChangePercentage > 0" class='price-chart-header__chg24h--positive'>+{{ priceChangePercentage }}%</span>
        <span v-else class='price-chart-header__chg24h--negative'>{{ priceChangePercentage }}%</span>
      </div>
      <a class="price-chart-header__link" :href="pricePageLink">
        {{ $t('kano.price-chart.link.to-price', {
          cryptoCode: props.cryptoCurrency.code,
          cryptoName: props.cryptoCurrency.name,
        }) }}
      </a>
    </div>
    <div class="price-chart-graph">
      <div class="price-chart-graph__controls">
        <FiatCurrencySelect v-model="selectedFiatCurrency" />
        <TimePeriodSelect v-model="selectedTimePeriod" />
      </div>
      <div v-if="chartData.loading" class="price-chart-graph__loader" />
      <ChartGraph
        v-else
        :fiatCurrency="selectedFiatCurrency"
        :chartData="chartData.items"
        :datePeriod="selectedTimePeriod"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ChartDataPoint,
  ChartTimePeriod,
  PriceChartCryptoCurrency,
  PriceChartCurrency
} from "@kano/types/crypto-price-chart";
import PricesClient from "@kano/services/clients/seo-client";
import { onMounted, ref, watch, computed } from "vue";
import ChartGraph from "@kano/components/CryptoPriceChart/Chart/ChartGraph.vue";
import FiatCurrencySelect from "@kano/components/CryptoPriceChart/Chart/FiatCurrencySelect.vue";
import TimePeriodSelect from "@kano/components/CryptoPriceChart/Chart/TimePeriodSelect.vue";
import { useMemoize } from "@vueuse/core";
import { useSimpleList } from "@kano/composables";

const props = defineProps<{
  cryptoCurrency: PriceChartCryptoCurrency,
  fiatCurrency: PriceChartCurrency,
  pricePageLink: string,
  formattedPrice: string,
}>();
const emit = defineEmits<{
  (event: 'fiatChanged'): void,
}>();

const selectedFiatCurrency = ref<PriceChartCurrency>(props.fiatCurrency);
const selectedTimePeriod = ref<ChartTimePeriod>(ChartTimePeriod.THREE_MONTHS);

const priceChangePercentage = computed(() => {
  if(!chartData.items || chartData.items.length < 2) {
    return 0;
  }

  const first = chartData.items[0].price;
  const last = chartData.items.at(-1)?.price || 0;

  if (first === 0) return 0;

  const change = ((last - first) / first) * 100;

  return change.toFixed(2);
})

const getChartData = useMemoize((cryptoCode, fiatCode, timePeriod) => PricesClient.getHistoricData('CCCAGG', cryptoCode, fiatCode, timePeriod));
const { state: chartData, fetch: fetchChartData } = useSimpleList<ChartDataPoint>({
  list: async () => {
    const response = await getChartData(props.cryptoCurrency.code, selectedFiatCurrency.value.code, selectedTimePeriod.value);

    return response.map((item) => {
      return {
        date: (new Date(item[0] * 1000)).toISOString(),
        price: item[1],
      }
    });
  },
});

watch([selectedFiatCurrency, selectedTimePeriod], ([newFiatCurrency], [oldFiatCurrency]) => {
  fetchChartData();

  if (oldFiatCurrency.code !== newFiatCurrency.code) {
    emit('fiatChanged', newFiatCurrency);
  }
});

onMounted(() => {
  fetchChartData();
});
</script>

<style scoped lang="scss">
.price-chart-graph__controls {
  display: flex;
  justify-content: end;
  gap: 8px;
}
</style>